import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { StarRating, RatingDelta, RatingSparkline } from "./StarRating";
import { LatestReviews } from "./LatestReviews";

const FN_BADGE = {
  Sales: "border-[hsl(var(--cx-gold))] text-[hsl(var(--cx-gold))]",
  Service: "border-[hsl(var(--cx-navy))] text-[hsl(var(--cx-navy))]",
  Combined: "bg-[hsl(var(--cx-navy))] text-white border-transparent",
};

export const RatingCard = ({ card, outletCode }) => {
  const label = card.combined ? "Combined" : card.function;
  const testBase = `rating-card-${outletCode}-${label.toLowerCase()}`;
  if (!card.linked) {
    return (
      <Card className="p-4 flex-1 min-w-[220px] border-dashed" data-testid={`${testBase}-unlinked`}>
        <div className="flex items-center justify-between gap-2">
          <Badge variant="outline" className={cn("text-[0.62rem]", FN_BADGE[label])}>{label}</Badge>
          <Badge variant="outline" className="text-[0.6rem] text-muted-foreground">Not linked</Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          No Google listing mapped yet — an admin can link it in Settings → Google Ratings.
        </p>
      </Card>
    );
  }
  return (
    <Card className="p-4 flex-1 min-w-[220px]" data-testid={testBase}>
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5">
          <Badge variant="outline" className={cn("text-[0.62rem]", FN_BADGE[label])}>{label}</Badge>
          {card.flagged && (
            <span title="Fetch failing 3+ days" data-testid={`${testBase}-flagged`}>
              <AlertTriangle size={13} className="text-[hsl(var(--status-amber))]" />
            </span>
          )}
        </span>
        <a href={card.google_url} target="_blank" rel="noreferrer"
          className="flex items-center gap-1 text-[0.65rem] text-muted-foreground hover:text-[hsl(var(--cx-navy))] underline-offset-2 hover:underline"
          data-testid={`${testBase}-google-link`}>
          View on Google <ExternalLink size={11} />
        </a>
      </div>

      <div className="flex items-baseline gap-2.5 mt-2.5">
        <span className="kpi-value text-3xl" data-testid={`${testBase}-value`}>
          {card.rating !== null && card.rating !== undefined ? card.rating.toFixed(1) : "—"}
        </span>
        <StarRating value={card.rating} />
      </div>
      <p className="text-xs text-muted-foreground tabular mt-0.5">
        {(card.total_reviews ?? 0).toLocaleString()} reviews
      </p>

      <div className="flex items-center gap-3 mt-2 flex-wrap">
        <span className="text-xs tabular font-medium" data-testid={`${testBase}-mtd`}>
          +{card.mtd_new} new review{card.mtd_new !== 1 ? "s" : ""} this month (MTD)
        </span>
        <span className="flex items-center gap-1 text-[0.65rem] text-muted-foreground">
          vs 1st: <RatingDelta delta={card.rating_delta} />
        </span>
      </div>
      {card.reviews_removed && (
        <p className="text-[0.65rem] text-[hsl(var(--status-amber))] mt-0.5" data-testid={`${testBase}-removed-note`}>
          Some reviews were removed by Google this month
        </p>
      )}

      <div className="mt-2">
        <RatingSparkline spark={card.spark} />
        <p className="text-[0.6rem] text-muted-foreground mt-0.5">3-month trend</p>
      </div>

      <p className={cn("text-[0.65rem] tabular mt-2",
        card.stale ? "text-[hsl(var(--status-amber))]" : "text-muted-foreground")}
        data-testid={`${testBase}-asof`}>
        {card.stale ? "Rating as of " : "As of "}{card.as_of_fmt}
      </p>

      <LatestReviews reviews={card.reviews} googleUrl={card.google_url} testBase={testBase} />
    </Card>
  );
};
